import { useEffect, useRef } from "react";

import { EXIT_SURVEY_IDLE_MS, EXIT_SURVEY_LEAVE_CART_PENDING, type ExitSurveyTriggerId } from "~/lib/exit-survey-gate";

function isCartPath(pathname: string) {
    return pathname === "/cart" || pathname.replace(/\/$/, "") === "/cart";
}

/**
 * Auto-triggers (scroll_depth removed — it interfered with map space/cart flows). Each calls tryOpen(id).
 */
export function useExitSurveyTriggers({
    tryOpen,
    baseEnabled,
    layout,
    pathname,
    itemCount,
    cartLoaded,
}: {
    tryOpen: (trigger: ExitSurveyTriggerId) => void;
    baseEnabled: boolean;
    layout: "store" | "checkout";
    pathname: string;
    itemCount: number;
    cartLoaded: boolean;
}) {
    const tryOpenRef = useRef(tryOpen);
    tryOpenRef.current = tryOpen;

    // --- leave_cart: persist “had items on /cart” across layout remounts (cart → checkout) ---
    useEffect(() => {
        try {
            if (isCartPath(pathname) && itemCount > 0) {
                sessionStorage.setItem(EXIT_SURVEY_LEAVE_CART_PENDING, "1");
            } else if (isCartPath(pathname)) {
                sessionStorage.removeItem(EXIT_SURVEY_LEAVE_CART_PENDING);
            }
        } catch {
            /* ignore */
        }
    }, [pathname, itemCount]);

    useEffect(() => {
        if (isCartPath(pathname) || !cartLoaded || !baseEnabled) return;
        try {
            if (sessionStorage.getItem(EXIT_SURVEY_LEAVE_CART_PENDING) === "1") {
                tryOpenRef.current("leave_cart");
                sessionStorage.removeItem(EXIT_SURVEY_LEAVE_CART_PENDING);
            }
        } catch {
            /* ignore */
        }
    }, [pathname, cartLoaded, baseEnabled]);

    // --- exit_intent: fine pointer only; mouseout on documentElement; cursor leaving top ---
    useEffect(() => {
        if (!baseEnabled) return;
        const mq = window.matchMedia("(pointer: fine)");
        if (!mq.matches) return;

        const onMouseOut = (e: MouseEvent) => {
            if (e.clientY > 0) return;
            tryOpenRef.current("exit_intent");
        };

        document.documentElement.addEventListener("mouseout", onMouseOut);
        return () => document.documentElement.removeEventListener("mouseout", onMouseOut);
    }, [baseEnabled]);

    // --- idle: store routes only; 45s inactivity; keydown / pointerdown / scroll on window reset ---
    useEffect(() => {
        if (!baseEnabled || layout !== "store") return;

        let timer: ReturnType<typeof window.setTimeout> | undefined;
        const schedule = () => {
            if (timer !== undefined) window.clearTimeout(timer);
            timer = window.setTimeout(() => {
                tryOpenRef.current("idle");
            }, EXIT_SURVEY_IDLE_MS);
        };

        schedule();
        const opts = { passive: true } as const;
        const onActivity = () => schedule();

        window.addEventListener("keydown", onActivity, opts);
        window.addEventListener("pointerdown", onActivity, opts);
        window.addEventListener("scroll", onActivity, opts);

        return () => {
            if (timer !== undefined) window.clearTimeout(timer);
            window.removeEventListener("keydown", onActivity);
            window.removeEventListener("pointerdown", onActivity);
            window.removeEventListener("scroll", onActivity);
        };
    }, [baseEnabled, layout]);
}
